import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import type { PostIndustryDto } from '@/services/ws-post-crm/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { formatIndustryOptionLabel } from '../helpers';
import { useIndustriesPostCrmOptions } from '../hooks/useIndustriesPostCrmOptions';

interface IndustryPostCrmFilterProps {
  value: Option<PostIndustryDto> | null;
  onChange: (value: SingleValue<Option<PostIndustryDto>>) => void;
  label?: string;
}

export const IndustryPostCrmFilter = memo(function IndustryPostCrmFilter({ value, onChange, label }: IndustryPostCrmFilterProps) {
  const [t] = useTranslation(['backend', 'common']);
  const [, { language }] = useTranslation(['backend']);
  const { options, pending: loading } = useIndustriesPostCrmOptions();

  const handleChange = useCallbackRef((newValue: SingleValue<Option<PostIndustryDto>>) => {
    onChange(newValue);
  });

  const isOptionSelected = useCallbackRef((a: Option<PostIndustryDto>, b: Option<PostIndustryDto>) => a.value.id === b.value.id);

  const translatedFilter = useMemo(
    () => (value ? { ...value, label: formatIndustryOptionLabel(value.value, language) } : null),
    [value, language],
  );

  return (
    <Filter<PostIndustryDto>
      label={label ?? t('common:INDUSTRY')}
      loading={loading}
      selected={translatedFilter}
      onChange={handleChange}
      options={options}
      dataTestId='industry-dropdown'
      optionSelected={isOptionSelected}
    />
  );
});
